/**
 * PR Celebration — imperative DOM-based overlay.
 * Called directly from proceedCompleteSet, no React state/render cycle.
 */

const DURATION = 60000;
const PARTICLE_COLORS = ['#FF5C00', '#FF8C42', '#EAB308', '#FFFFFF', '#FCD34D'];

function playPowerUpSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const noise = ctx.createBufferSource();
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.04, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    noise.buffer = buf;
    const ng = ctx.createGain(); ng.gain.value = 0.15;
    noise.connect(ng); ng.connect(ctx.destination); noise.start();
    [330, 440, 554, 880].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.type = i < 2 ? 'sawtooth' : 'square';
      osc.frequency.value = freq; g.gain.value = 0.07;
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (i * 0.08) + 0.15);
      osc.start(ctx.currentTime + i * 0.08);
      osc.stop(ctx.currentTime + (i * 0.08) + 0.15);
    });
    setTimeout(() => {
      try {
        const s = ctx.createOscillator(); const sg = ctx.createGain();
        s.connect(sg); sg.connect(ctx.destination);
        s.type = 'sine'; s.frequency.value = 1760; sg.gain.value = 0.05;
        sg.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
        s.start(); s.stop(ctx.currentTime + 0.3);
      } catch {}
    }, 300);
  } catch {}
}

let activeOverlay = null;

export function showPrCelebration(opts = {}) {
  // Accept both legacy positional args and new options object, so old callers don't break.
  if (typeof opts !== 'object' || opts === null) {
    // Legacy: showPrCelebration(weight, exerciseName, soundEnabled)
    opts = { weight: arguments[0], exerciseName: arguments[1], soundEnabled: arguments[2] };
  }
  const { weight, exerciseName, reps = 1, prType = 'weight', prevBest = 0, soundEnabled = false, onPostToFeed = null } = opts;
  // Bodyweight exercises progress by reps, not load — show "{reps} REPS" in green.
  const isRepsPr = prType === 'reps' || ((Number(weight) || 0) <= 0 && (Number(reps) || 0) > 0);
  // Optional i18n: callers may pass `t` (react-i18next). Falls back to English.
  const T = typeof opts.t === 'function' ? opts.t : (_k, fallback) => fallback;
  const L = {
    newPr: T('celebration.newPr', 'NEW PR!'),
    bodyweight: T('common.bodyweight', 'Bodyweight'),
    postToFeed: T('celebration.postToFeed', 'POST TO FEED'),
    share: T('celebration.share', 'SHARE'),
    copied: T('celebration.copied', 'COPIED!'),
    dismiss: T('celebration.dismiss', 'DISMISS'),
    shareText: isRepsPr
      ? T('celebration.prRepsTextTemplate', 'New rep PR! {{reps}} reps on {{exercise}} 🏆🔥')
          .replace('{{reps}}', String(reps)).replace('{{exercise}}', String(exerciseName))
      : T('celebration.prTextTemplate', 'New all-time PR! {{weight}} KG on {{exercise}} 🏆🔥')
          .replace('{{weight}}', String(weight)).replace('{{exercise}}', String(exerciseName)),
  };

  // Prevent stacking
  if (activeOverlay) {
    activeOverlay.remove();
    activeOverlay = null;
  }

  if (soundEnabled) setTimeout(playPowerUpSound, 50);

  const overlay = document.createElement('div');
  activeOverlay = overlay;
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 9999;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    background: linear-gradient(165deg, rgba(26,26,26,0.93) 0%, rgba(13,13,13,0.95) 40%, rgba(26,10,0,0.93) 70%, rgba(13,13,13,0.95) 100%);
    opacity: 0; transition: opacity 0.3s ease;
  `;

  // Pulsing orb
  const orb = document.createElement('div');
  orb.style.cssText = `
    position: absolute; top: 33%; left: 50%; transform: translate(-50%, -50%);
    width: 500px; height: 500px; border-radius: 50%; opacity: 0.25; pointer-events: none;
    background: radial-gradient(circle, #FF5C00 0%, transparent 70%);
    animation: pr-orb-pulse 3s ease-in-out infinite;
  `;
  overlay.appendChild(orb);

  // Shockwave
  const shock = document.createElement('div');
  shock.style.cssText = `
    position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
    width: 60px; height: 60px; border-radius: 50%;
    border: 2px solid rgba(255,92,0,0.6); box-shadow: 0 0 30px rgba(255,92,0,0.3);
    animation: pr-shockwave 0.7s ease-out forwards; animation-delay: 0.1s; opacity: 0;
  `;
  overlay.appendChild(shock);

  // Sparks
  for (let i = 0; i < 16; i++) {
    const angle = (360 / 16) * i + Math.random() * 10;
    const dist = 100 + Math.random() * 180;
    const spark = document.createElement('div');
    spark.style.cssText = `
      position: absolute; top: 50%; left: 50%;
      width: ${2 + Math.random() * 3}px; height: ${2 + Math.random() * 3}px;
      border-radius: 50%; background: #FF5C00; box-shadow: 0 0 8px rgba(255,92,0,0.6);
      animation: pr-spark-burst ${400 + Math.random() * 300}ms ease-out forwards;
      animation-delay: ${80 + Math.random() * 200}ms; opacity: 0;
      --spark-x: ${Math.cos(angle * Math.PI / 180) * dist}px;
      --spark-y: ${Math.sin(angle * Math.PI / 180) * dist}px;
    `;
    overlay.appendChild(spark);
  }

  // Card
  const card = document.createElement('div');
  card.style.cssText = `
    position: relative; z-index: 10; display: flex; flex-direction: column;
    align-items: center; width: 300px; padding: 32px;
  `;

  // Glass background
  const glass = document.createElement('div');
  glass.style.cssText = `
    position: absolute; inset: 0; border-radius: 16px;
    background: rgba(255,255,255,0.04); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255,92,0,0.2);
    box-shadow: 0 0 40px rgba(255,92,0,0.1), inset 0 1px 0 rgba(255,255,255,0.05);
    animation: pr-card-appear 0.3s ease-out forwards; animation-delay: 0.05s; opacity: 0;
  `;
  card.appendChild(glass);

  // Logo
  const logo = document.createElement('div');
  logo.style.cssText = 'position:relative;display:flex;align-items:center;gap:6px;margin-bottom:20px;animation:pr-stagger-in 0.7s ease-out forwards;animation-delay:0.1s;opacity:0;transform:translateY(8px);';
  logo.innerHTML = `<img src="/icon-192.png" alt="" style="width:18px;height:18px;border-radius:4px;"/>
    <span style="font-family:Barlow Condensed,sans-serif;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:rgba(255,255,255,0.5);">trainedby<span style="color:#FF5C00">RS</span></span>`;
  card.appendChild(logo);

  // Trophy
  const trophy = document.createElement('div');
  trophy.style.cssText = 'position:relative;margin-bottom:16px;animation:pr-stagger-in 0.7s ease-out forwards;animation-delay:0.15s;opacity:0;transform:translateY(8px) scale(0.75);';
  trophy.innerHTML = `<div style="width:64px;height:64px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#FF5C00,#FF8C42);box-shadow:0 0 40px rgba(255,92,0,0.4),0 0 80px rgba(255,92,0,0.15);">
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>
  </div>`;
  card.appendChild(trophy);

  // Title
  const title = document.createElement('h1');
  title.textContent = L.newPr;
  title.style.cssText = `
    position:relative;font-family:Barlow Condensed,sans-serif;font-size:2.25rem;font-weight:900;
    text-transform:uppercase;color:#FFF;text-align:center;
    text-shadow:0 0 20px rgba(255,92,0,0.5),0 2px 0 rgba(0,0,0,0.4);letter-spacing:-0.01em;
    animation:pr-slam 0.4s cubic-bezier(0.22,1.5,0.36,1) forwards;animation-delay:0.15s;opacity:0;transform:scale(3);
  `;
  card.appendChild(title);

  // Divider
  const divider = document.createElement('div');
  divider.style.cssText = 'display:flex;align-items:center;gap:10px;margin:8px 0 12px;animation:pr-stagger-in 0.7s ease-out forwards;animation-delay:0.35s;opacity:0;';
  divider.innerHTML = `<div style="width:40px;height:1px;background:linear-gradient(90deg,transparent,rgba(255,92,0,0.5));"></div>
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,92,0,0.6)" stroke-width="2"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>
    <div style="width:40px;height:1px;background:linear-gradient(270deg,transparent,rgba(255,92,0,0.5));"></div>`;
  card.appendChild(divider);

  // Big slam — "{weight} KG" (yellow) for weighted PRs, "{reps} REPS" (green) for bodyweight rep PRs
  const slamColor = isRepsPr ? '#4ADE80' : '#FCD34D';
  const slamGlow = isRepsPr ? 'rgba(74,222,128,0.4)' : 'rgba(252,211,77,0.4)';
  const weightEl = document.createElement('p');
  weightEl.textContent = isRepsPr ? `${reps} REPS` : `${weight} KG`;
  weightEl.style.cssText = `
    position:relative;font-family:Barlow Condensed,sans-serif;font-size:1.875rem;font-weight:900;
    color:${slamColor};text-align:center;
    text-shadow:0 0 15px ${slamGlow},0 2px 0 rgba(0,0,0,0.4);letter-spacing:0.05em;
    animation:pr-weight-slam 0.4s cubic-bezier(0.22,1.5,0.36,1) forwards;animation-delay:0.25s;opacity:0;transform:scale(2);
  `;
  card.appendChild(weightEl);

  if (isRepsPr) {
    const bwEl = document.createElement('p');
    bwEl.textContent = L.bodyweight;
    bwEl.style.cssText = `
      font-family:Barlow Condensed,sans-serif;font-size:0.7rem;color:rgba(255,255,255,0.55);
      text-transform:uppercase;letter-spacing:0.12em;margin-top:2px;text-align:center;
      animation:pr-stagger-in 0.7s ease-out forwards;animation-delay:0.35s;opacity:0;transform:translateY(6px);
    `;
    card.appendChild(bwEl);
  }

  // Exercise name
  const exEl = document.createElement('p');
  exEl.textContent = exerciseName;
  exEl.style.cssText = `
    font-family:Barlow Condensed,sans-serif;font-size:0.75rem;color:rgba(156,163,175,1);
    text-transform:uppercase;letter-spacing:0.15em;margin-top:6px;text-align:center;
    animation:pr-stagger-in 0.7s ease-out forwards;animation-delay:0.4s;opacity:0;transform:translateY(6px);
  `;
  card.appendChild(exEl);

  // Dismiss helper
  function dismissOverlay() {
    overlay.style.opacity = '0';
    setTimeout(() => {
      overlay.remove();
      if (activeOverlay === overlay) activeOverlay = null;
    }, 300);
  }

  // Post to Feed button (primary — only shown when onPostToFeed is provided)
  let postBtn = null;
  if (typeof onPostToFeed === 'function') {
    postBtn = document.createElement('button');
    postBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4 20-7Z"/></svg> ${L.postToFeed}`;
    postBtn.style.cssText = `
      display:flex;align-items:center;gap:8px;margin-top:20px;padding:10px 24px;border-radius:12px;
      font-family:Barlow Condensed,sans-serif;font-weight:700;text-transform:uppercase;font-size:0.75rem;
      letter-spacing:0.05em;background:linear-gradient(135deg,#FF5C00,#FF8C42);color:#FFF;border:none;cursor:pointer;
      box-shadow:0 0 20px rgba(255,92,0,0.4),0 4px 12px rgba(0,0,0,0.4);
      animation:pr-stagger-in 0.7s ease-out forwards;animation-delay:0.5s;opacity:0;transform:translateY(6px);
    `;
    postBtn.onclick = (e) => {
      e.stopPropagation();
      dismissOverlay();
      try { onPostToFeed({ weight, exerciseName, reps, prType, prevBest }); } catch (err) { console.error(err); }
    };
    card.appendChild(postBtn);
  }

  // Share button
  const shareBtn = document.createElement('button');
  shareBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg> ${L.share}`;
  shareBtn.style.cssText = `
    display:flex;align-items:center;gap:8px;margin-top:${postBtn ? '10px' : '20px'};padding:${postBtn ? '8px 20px' : '10px 24px'};border-radius:12px;
    font-family:Barlow Condensed,sans-serif;font-weight:700;text-transform:uppercase;font-size:0.75rem;
    letter-spacing:0.05em;
    background:${postBtn ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg,#FF5C00,#FF8C42)'};
    color:${postBtn ? 'rgba(255,255,255,0.85)' : '#FFF'};
    border:${postBtn ? '1px solid rgba(255,255,255,0.15)' : 'none'};cursor:pointer;
    box-shadow:${postBtn ? 'none' : '0 0 20px rgba(255,92,0,0.3),0 4px 12px rgba(0,0,0,0.4)'};
    animation:pr-stagger-in 0.7s ease-out forwards;animation-delay:${postBtn ? '0.55s' : '0.5s'};opacity:0;transform:translateY(6px);
  `;
  shareBtn.onclick = (e) => {
    e.stopPropagation();
    const text = L.shareText;
    if (navigator.share) {
      navigator.share({ title: 'trainedbyRS — New PR!', text, url: 'https://trainedbyrs.com' }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(text).then(() => {
        shareBtn.textContent = L.copied;
        setTimeout(() => { shareBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg> ${L.share}`; }, 1500);
      }).catch(() => {});
    }
  };
  card.appendChild(shareBtn);

  // Dismiss button
  const dismissBtn = document.createElement('button');
  dismissBtn.textContent = L.dismiss;
  dismissBtn.style.cssText = `
    margin-top:12px;min-height:44px;padding:8px 20px;border-radius:8px;border:none;cursor:pointer;
    font-family:Barlow Condensed,sans-serif;font-weight:600;text-transform:uppercase;font-size:10px;
    letter-spacing:0.05em;color:rgba(156,163,175,1);background:transparent;
    animation:pr-stagger-in 0.7s ease-out forwards;animation-delay:0.6s;opacity:0;transform:translateY(6px);
  `;
  dismissBtn.onclick = (e) => { e.stopPropagation(); dismissOverlay(); };
  card.appendChild(dismissBtn);

  overlay.appendChild(card);

  // Particles
  for (let i = 0; i < 40; i++) {
    const p = document.createElement('div');
    const c = PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)];
    const w = 3 + Math.random() * 5;
    const h = 8 + Math.random() * 14;
    p.style.cssText = `
      position:absolute;top:0;left:${Math.random() * 100}%;
      width:${w}px;height:${h}px;background:${c};
      transform:rotate(${Math.random() * 360}deg);
      ${Math.random() > 0.5 ? `box-shadow:0 0 4px ${c};` : ''}
      ${Math.random() > 0.5 ? 'clip-path:polygon(50% 0%,100% 50%,50% 100%,0% 50%);' : ''}
      animation:pr-particle-fall ${900 + Math.random() * 900}ms ease-in forwards;
      animation-delay:${200 + Math.random() * 500}ms;opacity:0;
    `;
    overlay.appendChild(p);
  }

  // Keyframes (inject once)
  if (!document.getElementById('pr-celebration-styles')) {
    const style = document.createElement('style');
    style.id = 'pr-celebration-styles';
    style.textContent = `
      @keyframes pr-orb-pulse { 0%,100% { transform:translate(-50%,-50%) scale(1);opacity:0.2; } 50% { transform:translate(-50%,-50%) scale(1.15);opacity:0.3; } }
      @keyframes pr-card-appear { 0% { opacity:0;transform:scale(0.9); } 100% { opacity:1;transform:scale(1); } }
      @keyframes pr-slam { 0% { opacity:0;transform:scale(3); } 50% { opacity:1;transform:scale(0.9); } 70% { transform:scale(1.08); } 100% { opacity:1;transform:scale(1); } }
      @keyframes pr-weight-slam { 0% { opacity:0;transform:scale(2); } 60% { opacity:1;transform:scale(0.92); } 80% { transform:scale(1.05); } 100% { opacity:1;transform:scale(1); } }
      @keyframes pr-stagger-in { to { opacity:1;transform:translateY(0) scale(1); } }
      @keyframes pr-shockwave { 0% { opacity:0.8;transform:translate(-50%,-50%) scale(0); } 100% { opacity:0;transform:translate(-50%,-50%) scale(7); } }
      @keyframes pr-spark-burst { 0% { opacity:1;transform:translate(0,0) scale(1); } 100% { opacity:0;transform:translate(var(--spark-x),var(--spark-y)) scale(0); } }
      @keyframes pr-particle-fall { 0% { opacity:1;transform:translateY(0) rotate(0deg); } 100% { opacity:0;transform:translateY(88vh) rotate(720deg); } }
    `;
    document.head.appendChild(style);
  }

  document.body.appendChild(overlay);

  // Fade in
  requestAnimationFrame(() => { overlay.style.opacity = '1'; });

  // Tap overlay to dismiss
  overlay.onclick = dismissOverlay;

  // Auto-dismiss
  setTimeout(dismissOverlay, DURATION);
}
